import { getAdminAccessToken, getAdminApiBaseUrl } from '@/lib/adminApi';
import type { PageAccess } from '@/features/employees/employeeRoleTypes';
import { resolveSessionRolePermissions } from '@/features/employees/employeeRolePermissions';

export interface ResolvedRolePermissions {
  isFullAccess: boolean;
  pagePermissions: Record<string, PageAccess>;
  platformPowers: Record<string, boolean>;
}

export interface SuperAdminMeIdentity {
  authenticatedUserSub: string;
  effectiveRole: string;
  superAdminLevel: string;
  employeeRoleKey?: string;
  employeeId?: string | null;
  isSuperAdmin: boolean;
  isImpersonating: boolean;
  resolvedAtEpochMs: number;
  rolePermissions?: ResolvedRolePermissions;
}

export type SuperAdminAccessCheckResult =
  | { status: 'approved'; identity: SuperAdminMeIdentity }
  | { status: 'denied'; httpStatus: number }
  | { status: 'unavailable'; httpStatus: number }
  | { status: 'error'; message: string };

const SUPER_ADMIN_ME_PATH =
  (process.env.REACT_APP_SA_SUPER_ADMIN_ME_PATH || '/sa/super-admin/me').replace(
    /\/+$/,
    '',
  );

/**
 * Authoritative Super Admin allow-list probe (kk-admin-sa-governance).
 * Requires an active row in kk-super-admin-user (shared-authz SA path).
 *
 * Domain routing must expose GET /sa/super-admin/me on the admin API host
 * (see admin-api docs/security/SA-API-HARDENING-TIER-C.md).
 */
export async function checkSuperAdminAccess(
  getToken?: () => Promise<string>,
): Promise<SuperAdminAccessCheckResult> {
  const baseUrl = getAdminApiBaseUrl();
  const url = `${baseUrl}${SUPER_ADMIN_ME_PATH.startsWith('/') ? '' : '/'}${SUPER_ADMIN_ME_PATH}`;

  try {
    const token = await getAdminAccessToken(getToken);
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });

    if (response.status === 403 || response.status === 401) {
      return { status: 'denied', httpStatus: response.status };
    }

    if (response.status === 404 || response.status === 405) {
      return { status: 'unavailable', httpStatus: response.status };
    }

    if (!response.ok) {
      return {
        status: 'error',
        message: `Access verification failed (${response.status})`,
      };
    }

    const body = (await response.json()) as {
      success?: boolean;
      data?: SuperAdminMeIdentity;
    };

    if (body?.success === true && body.data?.isSuperAdmin === true) {
      const identity = body.data;
      const rolePermissions = resolveSessionRolePermissions(identity);
      return {
        status: 'approved',
        identity: { ...identity, rolePermissions: rolePermissions ?? undefined },
      };
    }

    return { status: 'denied', httpStatus: response.status };
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Access verification failed';
    return { status: 'error', message };
  }
}
